import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DataGridPremium as DataGrid, useGridApiRef } from "@mui/x-data-grid-premium";
import {
  Add,
  AttachMoney,
  Clear,
  ContentCopy,
  Delete,
  Domain,
  Edit,
  EventAvailable,
  Money,
  Numbers,
  Percent,
  ShoppingBasket,
} from "@mui/icons-material";
import { useApi } from "../../utils/Api";
import { useLocation, useNavigate } from "react-router-dom";
import { dataGridLang } from "../MemToolBar";
import { ToolBar } from "../ToolBar";
import DotMenu from "../DotMenu";
import { IconAndLabelColumnHeader } from "../../utils/dataGrid";
import SpecialPriceDialog from "./SpecialPriceDialog";
import CopySpecialPricesDialog from "./CopySpecialPricesDialog";
import { formatDate } from "../../utils/dateTimeFormat";
import { formatMoney } from "../../utils/formatMoney";
import YesOrNoDialog from "../YesOrNoDialog";
import { useSnackbar } from "notistack";
import LinkMenu from "../LinkMenu";
import { AlertDialog } from "../AlertDialog";
import { groupBy } from "lodash";

function splitRowId(rowId) {
  const [partId, actorId, currency] = rowId.split("_");
  return { partId: Number(partId), actorId: Number(actorId), currency };
}

function getAdditionalTitle(partId, partDescription, partNo, actorId, actorName) {
  if (partId && partDescription) {
    return `${partNo ?? ""} ${partDescription}`;
  } else if (actorId && actorName) {
    return actorName;
  }
}

function createLongErrorMessage(results) {
  const failedResults = results.filter((r) => !r.response.isSuccessful);
  if (failedResults.length > 0) {
    const errorMessage = failedResults
      .map(({ response, specialPrice }) => {
        const message = response.errorMessageTranslationKey
          ? Translate.get(response.errorMessageTranslationKey)
          : Translate.get("SomethingFailed");
        const part = `${specialPrice.partNo ?? ""} ${specialPrice.partDescription ?? ""} (id: ${specialPrice.partId})`;
        const actor = `${specialPrice.actorName ?? ""} (id: ${specialPrice.actorId})`;
        return `${part}, ${actor}:\n${message}`;
      })
      .join("\n\n");
    return errorMessage;
  } else {
    return null;
  }
}

export default function SpecialPriceGridDialogContent({
  partId,
  partNo,
  partDescription,
  actorId,
  actorName,
  onClose,
}) {
  if (partId && actorId) {
    throw new Error("Use either partId or actorId");
  }

  const [specialPrices, setSpecialPrices] = useState([]);
  const [specialPriceToEdit, setSpecialPriceToEdit] = useState(null);
  const [specialPricesToEdit, setSpecialPricesToEdit] = useState(null);
  const [specialPriceToDelete, setSpecialPriceToDelete] = useState(null);
  const [specialPricesToDelete, setSpecialPricesToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSpecialPriceRowIds, setSelectedSpecialPriceRowIds] = useState({ type: "include", ids: new Set() });
  const [longErrorMessage, setLongErrorMessage] = useState(null);
  const [showCopySpecialPrices, setShowCopySpecialPrices] = useState(false);

  const api = useApi();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(900));
  const localizedTextsMap = dataGridLang();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const location = useLocation();
  const closeAndReload = () => {
    //Soft reload with redirect
    const hash = window.location.hash.endsWith("#dialog")
      ? window.location.hash.substring(0, window.location.hash.length - 7)
      : window.location.hash;
    const loc = encodeURIComponent(`${location.pathname}${location.search}${hash}`);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  const loadSpecialPrices = useCallback(
    async (partId, actorId) => {
      setIsLoading(true);
      const url = partId
        ? `${import.meta.env.VITE_MAIN_URL}warehouse/parts/${partId}/special-prices`
        : `${import.meta.env.VITE_MAIN_URL}actors/${actorId}/special-prices`;
      const response = await api.fetch(url, false, "GET");
      if (response.isSuccessful) {
        const sortedSpecialPrices = partId
          ? response.value.sort((a, b) => a.actorName.localeCompare(b.actorName))
          : response.value.sort((a, b) => a.partDescription.localeCompare(b.partDescription));
        setSpecialPrices(sortedSpecialPrices);
        setSelectedSpecialPriceRowIds({ type: "include", ids: new Set() });
      }
      setIsLoading(false);
    },
    [api],
  );

  const deleteSpecialPrice = useCallback(
    async (specialPrice, isMultiAction) => {
      const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/special-prices`;
      const response = !isMultiAction
        ? await api.fetch(url, specialPrice, "DELETE")
        : await api.fetchWithOverride(url, specialPrice, "DELETE", () => true); // To avoid automatically displaying snackbar on error
      return response;
    },
    [api],
  );

  const handleDeleteSingleSpecialPrice = useCallback(
    async (specialPrice) => {
      const response = await deleteSpecialPrice(specialPrice, false);
      if (response.isSuccessful) {
        const successMessage = enqueueSnackbar(Translate.get("Ready"), {
          variant: "success",
          autoHideDuration: 6000,
          onClick: () => closeSnackbar(successMessage),
        });
      }

      setSpecialPriceToDelete(null);
      await loadSpecialPrices(partId, actorId);
    },
    [deleteSpecialPrice, loadSpecialPrices, actorId, partId],
  );

  const handleDeleteMultipleSpecialPrice = useCallback(
    async (specialPrices) => {
      const results = [];

      for (let specialPrice of specialPrices) {
        const response = await deleteSpecialPrice(specialPrice, true);
        results.push({ response, specialPrice });
      }

      const allOk = results.every((r) => r.response.isSuccessful);
      if (allOk) {
        const successMessage = enqueueSnackbar(Translate.get("Ready"), {
          variant: "success",
          autoHideDuration: 6000,
          onClick: () => closeSnackbar(successMessage),
        });
      } else {
        const errorMessage = createLongErrorMessage(results);
        setLongErrorMessage(errorMessage);
      }

      setSpecialPricesToDelete(null);
      await loadSpecialPrices(partId, actorId);
    },
    [deleteSpecialPrice, loadSpecialPrices, actorId, partId],
  );

  useEffect(() => {
    loadSpecialPrices(partId, actorId);
  }, [partId, actorId, loadSpecialPrices]);

  const getDotMenuActions = useCallback((specialPrice) => {
    const menudata = [];

    menudata.push({
      id: "edit-special-price",
      icon: <Edit />,
      text: Translate.get("EditSpecialPrice"),
      onClick: () => setSpecialPriceToEdit(specialPrice),
    });

    menudata.push({
      id: "delete-special-price",
      icon: <Delete />,
      text: Translate.get("Delete"),
      onClick: () => setSpecialPriceToDelete(specialPrice),
    });

    return menudata;
  }, []);

  const getMultiMenuActions = useCallback((specialPrices) => {
    const menudata = [];

    const firstSpecialPrice = specialPrices[0];
    const canMultiEdit = specialPrices.every(
      (sp) => sp.partId === firstSpecialPrice.partId && sp.currency === firstSpecialPrice.currency,
    );

    menudata.push({
      id: "multi-edit-special-prices",
      icon: <Edit />,
      text: Translate.get("EditSpecialPrices"),
      disabled: !canMultiEdit,
      disabledTooltip: Translate.get("MultiEditSpecialPriceDisabledMessage"),
      onClick: () => setSpecialPricesToEdit(specialPrices),
    });

    menudata.push({
      id: "multi-delete-special-prices",
      icon: <Delete />,
      text: Translate.get("Delete"),
      onClick: () => setSpecialPricesToDelete(specialPrices),
    });

    return menudata;
  }, []);

  const columns = useMemo(
    () => [
      ...(!partId
        ? [
            {
              field: "partNo",
              flex: 0.3,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("PartNoLabel"),
              renderHeader: () => <IconAndLabelColumnHeader icon={Numbers} label={Translate.get("PartNoLabel")} />,
            },
            {
              field: "partDescription",
              flex: 1,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("PartLabel"),
              renderHeader: () => <IconAndLabelColumnHeader icon={ShoppingBasket} label={Translate.get("PartLabel")} />,
            },
          ]
        : []),
      ...(!actorId
        ? [
            {
              field: "actorName",
              flex: 1,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("Company"),
              renderHeader: () => <IconAndLabelColumnHeader icon={Domain} label={Translate.get("Company")} />,
            },
          ]
        : []),
      {
        field: "fixedPercent",
        flex: 0.3,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("SpecialPriceFixedPercent"),
        valueGetter: (value, row, column, apiRef) => {
          if (!value) {
            return undefined;
          }
          const percent = `${value * 100} %`;
          return row.finalPrice != null ? `${percent} (${formatMoney(row.finalPrice, null, row.currency)})` : percent;
        },
        renderHeader: () => (
          <IconAndLabelColumnHeader icon={Percent} label={Translate.get("SpecialPriceFixedPercent")} />
        ),
      },
      {
        field: "fixedPrice",
        flex: 0.3,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("SpecialPriceFixedPrice"),
        valueGetter: (value, row, column, apiRef) => (value ? value : undefined),
        renderHeader: () => <IconAndLabelColumnHeader icon={Money} label={Translate.get("SpecialPriceFixedPrice")} />,
      },
      {
        field: "currency",
        flex: 0.3,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("CurrencyLabel"),
        renderHeader: () => <IconAndLabelColumnHeader icon={AttachMoney} label={Translate.get("CurrencyLabel")} />,
      },
      {
        field: "regDate",
        flex: 0.4,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("LatestUpdateDate"),
        valueGetter: (value, row, column, apiRef) => formatDate(row.updDate ?? row.regDate),
        renderHeader: () => (
          <IconAndLabelColumnHeader icon={EventAvailable} label={Translate.get("LatestUpdateDate")} />
        ),
      },
      {
        field: "dotMenu",
        flex: 0.3,
        headerName: "",
        sortable: false,
        disableColumnMenu: true,
        filterable: false,
        hideable: false,
        headerClassName: "hideSeparator",
        align: "right",
        renderCell: (params) => <DotMenu content={getDotMenuActions(params.row)} horizontal />,
      },
    ],
    [getDotMenuActions, actorId, partId],
  );

  function getSelectionActions() {
    // return [];
    const selections = [...selectedSpecialPriceRowIds.ids].map((rowId) => splitRowId(rowId));
    const selectedSpecialPrices = specialPrices.filter((sp) =>
      selections.some(
        (selected) =>
          selected.partId === sp.partId && selected.actorId === sp.actorId && selected.currency === sp.currency,
      ),
    );
    return getMultiMenuActions(selectedSpecialPrices);
  }

  return (
    <>
      {(specialPriceToEdit || specialPricesToEdit) && (
        <SpecialPriceDialog
          specialPrice={specialPriceToEdit}
          specialPrices={specialPricesToEdit}
          onSaved={async () => {
            setSpecialPriceToEdit(null);
            setSpecialPricesToEdit(null);
            await loadSpecialPrices(partId, actorId);
          }}
          onClose={() => {
            setSpecialPriceToEdit(null);
            setSpecialPricesToEdit(null);
          }}
        />
      )}
      {showCopySpecialPrices && (
        <CopySpecialPricesDialog
          targetActorId={actorId}
          targetActorName={actorName}
          onClose={() => setShowCopySpecialPrices(false)}
          onSaved={async () => {
            setShowCopySpecialPrices(false);
            await loadSpecialPrices(partId, actorId);
          }}
        />
      )}
      <DialogTitle>
        {Translate.get("SpecialPrices")}
        {getAdditionalTitle(partId, partDescription, partNo, actorId, actorName)
          ? ` - ${getAdditionalTitle(partId, partDescription, partNo, actorId, actorName)}`
          : undefined}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid size={12}>
            {selectedSpecialPriceRowIds && selectedSpecialPriceRowIds.ids.size > 0 && (
              <Toolbar className="specialPriceToolbar">
                <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box>{`${selectedSpecialPriceRowIds.ids.size} ${Translate.get("ePort2Selected")}`}</Box>
                    <LinkMenu content={getSelectionActions()} />
                  </Stack>
                  <Tooltip title={Translate.get("DeselectAll")}>
                    <IconButton onClick={() => setSelectedSpecialPriceRowIds({ type: "include", ids: new Set() })}>
                      <Clear />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Toolbar>
            )}
            <DataGrid
              showToolbar
              apiRef={apiRef}
              className={"dgDynIconText" + (specialPrices.length === 0 ? " hideOverlayWrapper" : "")}
              loading={isLoading}
              columns={columns}
              rows={specialPrices}
              getRowId={(row) => `${row.partId}_${row.actorId}_${row.currency}`}
              onRowSelectionModelChange={(newSelectedSpecialPrices) => {
                setSelectedSpecialPriceRowIds(newSelectedSpecialPrices);
              }}
              rowSelectionModel={selectedSpecialPriceRowIds}
              localeText={localizedTextsMap}
              autoHeight
              disableDensitySelector
              disableColumnMenu
              hideFooter
              hideFooterSelectedRowCount
              slots={{
                toolbar: () =>
                  ToolBar({
                    showExport: true,
                    exportFileName: (actorId
                      ? `${Translate.get("SpecialPrices")}_${actorName}`
                      : Translate.get("SpecialPrices")
                    )
                      .replaceAll(" ", "_")
                      .toLowerCase(),
                    apiRef,
                  }),
                noRowsOverlay: () => <div></div>,
                noResultsOverlay: () => <div></div>,
              }}
            />
          </Grid>
          <Grid marginLeft={2} size={12}>
            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => setSpecialPriceToEdit({ actorId, actorName, partId, partDescription, partNo })}
                startIcon={<Add />}
              >
                {Translate.get("Add")}
              </Button>
              {actorId && (
                <Button onClick={() => setShowCopySpecialPrices(true)} startIcon={<Add />}>
                  {Translate.get("CopySpecialPrices")}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => (onClose ? onClose() : closeAndReload())}>{Translate.get("Close")}</Button>
      </DialogActions>
      <YesOrNoDialog
        open={!!specialPriceToDelete || !!specialPricesToDelete}
        title={Translate.get("Delete")}
        content={
          specialPriceToDelete ? Translate.get("SureDeleteSpecialPrice") : Translate.get("SureDeleteSpecialPrices")
        }
        onNo={() => {
          setSpecialPriceToDelete(null);
          setSpecialPricesToDelete(null);
        }}
        onYes={async () =>
          specialPriceToDelete
            ? await handleDeleteSingleSpecialPrice(specialPriceToDelete)
            : await handleDeleteMultipleSpecialPrice(specialPricesToDelete)
        }
        noText={Translate.get("No")}
        yesText={Translate.get("Yes")}
      />
      <AlertDialog
        titleText={Translate.get("SomethingFailed")}
        bodyText={longErrorMessage}
        buttonText={Translate.get("Ok")}
        open={!!longErrorMessage}
        onClose={() => setLongErrorMessage(null)}
      />
    </>
  );
}
